/* Noam Drive — Documents (Phase 1)
 *
 * Module additif : barre quota + grid de fichiers + upload drag&drop.
 * Pas de dossiers en Phase 1 côté UI (backend supporte). Filtres par
 * module source. Plan gating géré côté backend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiUpload } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  FolderOpen, Upload, Download, Trash2, FileText, Image as ImageIcon,
  Search, Loader2, AlertTriangle, X, Eye, Sparkles, Check, ArrowRight, RefreshCw,
} from "lucide-react";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import ShareDocModal from "@/components/ShareDocModal";
import ContextScopeBanner from "@/components/ContextScopeBanner";
import { Link2, Inbox, ExternalLink } from "lucide-react";

const AI_CATEGORIES = {
  courtage: "Courtage — immobilier",
  analyses: "Analyse crédit",
  risques: "Risques",
  juridique: "Juridique / Contrats",
  finance: "Finance",
  autre: "Autre",
};
const AI_TARGETS = { courtage: "/app/courtage", analyses: "/app/analyses" };

function isPreviewable(mime) {
  return mime && (mime.startsWith("image/") || mime === "application/pdf");
}

const MODULE_SOURCES = [
  "manual", "risques", "courtage", "immobilier", "negociation",
  "annuaire", "pipeline", "analyses", "ontologie",
];

function fmtBytes(n) {
  if (n == null || n === undefined) return "—";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} Go`;
}

function iconForMime(mime) {
  if (!mime) return FileText;
  if (mime.startsWith("image/")) return ImageIcon;
  return FileText;
}

export default function Documents() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState("");
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [shareDoc, setShareDoc] = useState(null);
  const [sharedWithMe, setSharedWithMe] = useState([]);
  const [ctxScope, setCtxScope] = useState(null);
  const visibleItems = ctxScope ? items.filter((d) => ctxScope.ids.has(d.id)) : items;
  const fileInputRef = useRef(null);
  const dragRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [docsRes, quotaRes] = await Promise.all([
        api.get("/workspace/documents", {
          params: {
            module_source: filterSource || undefined,
            search: search || undefined,
          },
        }),
        api.get("/workspace/documents/quota"),
      ]);
      setItems(docsRes.data?.items || []);
      setQuota(quotaRes.data || null);
    } catch (e) {
      notify.error(fmtError(e, "Chargement Drive impossible"));
    } finally {
      setLoading(false);
    }
  }, [filterSource, search]);

  useEffect(() => { load(); }, [load]);

  // Poll pendant qu'une analyse IA ou un routage tourne en tâche de fond.
  useEffect(() => {
    const busy = items.some(
      (d) => d.ai_suggestion?.status === "pending" || d.ai_routing?.status === "running"
    );
    if (!busy) return undefined;
    const timer = setInterval(async () => {
      try {
        const r = await api.get("/workspace/documents", {
          params: { module_source: filterSource || undefined, search: search || undefined },
        });
        setItems(r.data?.items || []);
      } catch { /* silencieux */ }
    }, 4000);
    return () => clearInterval(timer);
  }, [items, filterSource, search]);

  const handleRoute = async (doc, module) => {
    try {
      const r = await api.post(`/workspace/documents/${doc.id}/route`, { module });
      if (r.data?.status === "running") {
        notify.success(t("saas.axp2.doc_creating"));
      } else {
        notify.success(`Classé dans « ${AI_CATEGORIES[module] || module} ».`);
      }
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Routage impossible"));
    }
  };

  useEffect(() => {
    api.get("/workspace/documents/shares/shared-with-me")
      .then((r) => setSharedWithMe(r.data?.items || []))
      .catch(() => {});
  }, []);

  const handleReanalyze = async (doc) => {
    try {
      await api.post(`/workspace/documents/${doc.id}/reanalyze`);
      notify.success(t("saas.axp2.doc_reanalyzed"));
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Analyse impossible"));
    }
  };

  const handleUpload = async (files, sourceModule = "manual") => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("module_source", sourceModule);
        try {
          await apiUpload("/workspace/documents", fd);
          notify.success(`« ${file.name} » importé.`);
        } catch (e) {
          notify.error(fmtError(e, `Échec : ${file.name}`));
        }
      }
      await load();
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragRef.current?.classList.remove("border-[#378ADD]");
    handleUpload(Array.from(e.dataTransfer.files || []));
  };

  const onDragOver = (e) => {
    e.preventDefault();
    dragRef.current?.classList.add("border-[#378ADD]");
  };

  const onDragLeave = () => {
    dragRef.current?.classList.remove("border-[#378ADD]");
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer définitivement « ${doc.name} » ?`)) return;
    try {
      await api.delete(`/workspace/documents/${doc.id}`);
      notify.success(t("saas.axp2.doc_deleted"));
      await load();
    } catch (e) {
      notify.error(fmtError(e, "Suppression impossible"));
    }
  };

  const handleDownload = (doc) => {
    const url = `${api.defaults.baseURL}/workspace/documents/${doc.id}/download`;
    window.open(url, "_blank");
  };

  const quotaPercent = useMemo(() => {
    if (!quota || quota.total_bytes == null) return 0;
    if (quota.total_bytes === 0) return 100;
    return Math.min(100, Math.round((quota.used_bytes / quota.total_bytes) * 100));
  }, [quota]);

  const quotaColor =
    quota?.alert_level === "full" ? "#E24B4A"
    : quota?.alert_level === "warn" ? "#EF9F27"
    : "#1D9E75";

  return (
    <div className="space-y-5" data-testid="documents-root">
      <div>
        <div className="noam-overline">{t("docs.overline", { defaultValue: "Stockage centralisé" })}</div>
        <h1 className="noam-display text-2xl font-bold tracking-tight">{t("docs.title", { defaultValue: "Noam Drive" })}</h1>
        <p className="text-zinc-400 text-sm mt-1">
          {t("docs.subtitle", { defaultValue: "Tous vos documents — par module, par projet, par dossier. Stockage GridFS sécurisé." })}
        </p>
      </div>

      {/* V23.15.36 — Doctrine 3Q · quota ou documents à rattacher */}
      <ThreeQPageHeader endpoint="/workspace/documents/top-priority" />

      {/* Quota bar */}
      {quota && (
        <div className="noam-card p-4" data-testid="docs-quota">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-xs text-zinc-400">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-2">{t("docs.plan", { defaultValue: "Plan" })}</span>
              <span className="uppercase font-semibold text-zinc-200">{quota.plan}</span>
              {quota.extra_gb > 0 && (
                <span className="ml-2 text-[10px] text-[#EF9F27]">+{quota.extra_gb} Go ext.</span>
              )}
            </div>
            <div className="text-xs font-mono" style={{ color: quotaColor }} data-testid="docs-quota-text">
              {fmtBytes(quota.used_bytes)} / {quota.total_bytes == null ? "∞" : fmtBytes(quota.total_bytes)}
            </div>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{ width: `${quotaPercent}%`, background: quotaColor }}
              data-testid="docs-quota-bar"
            />
          </div>
          {quota.alert_level === "warn" && (
            <div className="mt-2 text-[11px] text-[#EF9F27] flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {t("docs.quota_warn", { defaultValue: "Quota >80 %. Pensez à libérer de l'espace ou à étendre votre plan." })}
            </div>
          )}
          {quota.alert_level === "full" && (
            <div className="mt-2 text-[11px] text-[#E24B4A] flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" /> {t("docs.quota_full", { defaultValue: "Quota atteint — les nouveaux uploads seront refusés." })}
            </div>
          )}
        </div>
      )}

      {/* Filters + actions */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="docs-toolbar">
        <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-md px-2 py-1.5 flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("docs.search_ph", { defaultValue: "Rechercher par nom, tag, description…" })}
            className="bg-transparent border-0 outline-none text-xs w-full text-zinc-200"
            data-testid="docs-search"
          />
        </div>
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-zinc-200"
          data-testid="docs-filter-source"
        >
          <option value="">{t("docs.filter_all", { defaultValue: "Tous les modules" })}</option>
          {MODULE_SOURCES.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || quota?.alert_level === "full"}
          className="btn-primary text-xs disabled:opacity-50"
          data-testid="docs-upload-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {t("docs.upload_btn", { defaultValue: "Importer" })}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files || []))}
          data-testid="docs-file-input"
        />
      </div>

      {/* Drag and drop zone */}
      <div
        ref={dragRef}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className="border-2 border-dashed border-white/10 rounded-md p-6 text-center text-xs text-zinc-500 transition-colors"
        data-testid="docs-dropzone"
      >
        {t("docs.dropzone", { defaultValue: "Glissez-déposez des fichiers ici (PDF, images, Office, etc.)" })}
      </div>

      {/* Partagés avec moi */}
      {sharedWithMe.length > 0 && (
        <div className="noam-card p-4" data-testid="docs-shared-with-me">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
            <Inbox className="w-3.5 h-3.5 text-[#378ADD]" /> Partagés avec moi
          </div>
          <div className="space-y-1.5">
            {sharedWithMe.map((s) => (
              <a key={s.token} href={`/share/${s.token}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-2 text-xs text-zinc-300 hover:text-white p-1.5 rounded hover:bg-white/5"
                data-testid={`docs-shared-item-${s.token}`}>
                <ExternalLink className="w-3 h-3 text-[#378ADD]" />
                <span className="font-medium truncate">{s.doc_name}</span>
                <span className="text-zinc-500 text-[10px]">de {s.from_org || s.created_by} · {(s.created_at || "").slice(0, 10)}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Grid */}
      <ContextScopeBanner module="documents" onScope={setCtxScope} />
      <div className="noam-card p-0">
        {loading ? (
          <div className="p-6 text-zinc-500 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            <FolderOpen className="w-10 h-10 mx-auto text-[#378ADD] opacity-40 mb-3" />
            {ctxScope
              ? t("docs.empty_ctx", { defaultValue: "Aucun document relié à ce contexte." })
              : t("docs.empty", { defaultValue: "Aucun document. Importez votre premier fichier." })}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-3" data-testid="docs-grid">
            {visibleItems.map((d) => {
              const Icon = iconForMime(d.mime_type);
              return (
                <div key={d.id} className="bg-black/20 border border-white/5 rounded-md p-3 hover:border-white/15 transition-colors group" data-testid={`docs-card-${d.id}`}>
                  <div className="flex items-start gap-2 mb-2">
                    <div className="w-9 h-9 rounded bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#378ADD]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => setPreviewDoc(d)}
                        className="text-[13px] text-zinc-100 font-medium truncate block w-full text-left hover:text-[#378ADD] transition-colors"
                        title={d.name}
                        data-testid={`docs-name-${d.id}`}
                      >
                        {d.name}
                      </button>
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {fmtBytes(d.size_bytes)} · {(d.uploaded_at || "").slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap mt-2">
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">
                      {d.module_source}
                    </span>
                    {(d.tags || []).slice(0, 2).map((tag) => (
                      <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Drive IA — suggestion / routage */}
                  {d.ai_suggestion?.status === "pending" && (
                    <div className="mt-2 text-[10px] text-[#378ADD] flex items-center gap-1.5" data-testid={`docs-ai-pending-${d.id}`}>
                      <Loader2 className="w-3 h-3 animate-spin" /> Analyse IA en cours…
                    </div>
                  )}
                  {d.ai_suggestion?.status === "failed" && !d.ai_routing && (
                    <div className="mt-2 flex items-center gap-2 text-[10px] text-zinc-500" data-testid={`docs-ai-failed-${d.id}`}>
                      <AlertTriangle className="w-3 h-3 text-[#EF9F27]" /> Analyse IA indisponible
                      <button onClick={() => handleReanalyze(d)} className="text-[#378ADD] hover:underline flex items-center gap-0.5">
                        <RefreshCw className="w-2.5 h-2.5" /> Relancer
                      </button>
                    </div>
                  )}
                  {d.ai_suggestion?.status === "done" && !d.ai_routing && (
                    <div className="mt-2 bg-[#378ADD]/5 border border-[#378ADD]/20 rounded p-2" data-testid={`docs-ai-suggestion-${d.id}`}>
                      <div className="text-[10px] text-[#378ADD] flex items-center gap-1 font-medium">
                        <Sparkles className="w-3 h-3" /> IA suggère : {AI_CATEGORIES[d.ai_suggestion.module] || d.ai_suggestion.module}
                      </div>
                      {d.ai_suggestion.summary && (
                        <div className="text-[10px] text-zinc-400 mt-1 line-clamp-2">{d.ai_suggestion.summary}</div>
                      )}
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <button
                          onClick={() => handleRoute(d, d.ai_suggestion.module)}
                          className="text-[10px] px-2 py-1 rounded bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/25 flex items-center gap-1 font-medium"
                          data-testid={`docs-ai-accept-${d.id}`}
                        >
                          <Check className="w-3 h-3" /> Oui, classer
                        </button>
                        <select
                          defaultValue=""
                          onChange={(e) => { if (e.target.value) handleRoute(d, e.target.value); }}
                          className="text-[10px] bg-black/30 border border-white/10 rounded px-1.5 py-1 text-zinc-400"
                          data-testid={`docs-ai-other-${d.id}`}
                        >
                          <option value="" disabled>{t("saas.axp2.doc_other_cat")}</option>
                          {Object.entries(AI_CATEGORIES)
                            .filter(([k]) => k !== d.ai_suggestion.module)
                            .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                      </div>
                    </div>
                  )}
                  {d.ai_routing?.status === "running" && (
                    <div className="mt-2 text-[10px] text-[#EF9F27] flex items-center gap-1.5" data-testid={`docs-ai-routing-${d.id}`}>
                      <Loader2 className="w-3 h-3 animate-spin" /> Création de la fiche {AI_CATEGORIES[d.ai_routing.module] || d.ai_routing.module}…
                    </div>
                  )}
                  {d.ai_routing?.status === "done" && (
                    <button
                      onClick={() => AI_TARGETS[d.ai_routing.module] && navigate(AI_TARGETS[d.ai_routing.module])}
                      className={`mt-2 text-[10px] px-2 py-1 rounded bg-[#1D9E75]/10 border border-[#1D9E75]/30 text-[#1D9E75] flex items-center gap-1 ${AI_TARGETS[d.ai_routing.module] ? "hover:bg-[#1D9E75]/20 cursor-pointer" : "cursor-default"}`}
                      data-testid={`docs-ai-routed-${d.id}`}
                    >
                      <ArrowRight className="w-3 h-3" /> {AI_CATEGORIES[d.ai_routing.module] || d.ai_routing.module}
                      {d.ai_routing.entity_label ? ` · ${d.ai_routing.entity_label}` : ""}
                    </button>
                  )}
                  {d.ai_routing?.status === "failed" && (
                    <div className="mt-2 flex items-center gap-2 text-[10px] text-[#E24B4A]" data-testid={`docs-ai-route-failed-${d.id}`}>
                      <AlertTriangle className="w-3 h-3" /> Échec création fiche
                      <button onClick={() => handleRoute(d, d.ai_routing.module)} className="text-[#378ADD] hover:underline flex items-center gap-0.5">
                        <RefreshCw className="w-2.5 h-2.5" /> Réessayer
                      </button>
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setPreviewDoc(d)}
                      className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
                      data-testid={`docs-preview-${d.id}`}
                    >
                      <Eye className="w-3 h-3" /> Aperçu
                    </button>
                    <button
                      onClick={() => setShareDoc(d)}
                      className="text-[10px] text-zinc-400 hover:text-[#378ADD] flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
                      data-testid={`docs-share-${d.id}`}
                    >
                      <Link2 className="w-3 h-3" /> Partager
                    </button>
                    <button
                      onClick={() => handleDownload(d)}
                      className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5"
                      data-testid={`docs-download-${d.id}`}
                    >
                      <Download className="w-3 h-3" /> Télécharger
                    </button>
                    <button
                      onClick={() => handleDelete(d)}
                      className="text-[10px] text-zinc-500 hover:text-red-400 flex items-center gap-1 px-2 py-1 rounded hover:bg-white/5 ml-auto"
                      data-testid={`docs-delete-${d.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {shareDoc && <ShareDocModal doc={shareDoc} onClose={() => setShareDoc(null)} />}

      {/* Visionneuse de document */}
      {previewDoc && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col"
          onClick={() => setPreviewDoc(null)}
          data-testid="docs-viewer-overlay"
        >
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 bg-[#0B0F14]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="min-w-0">
              <div className="text-sm text-zinc-100 font-medium truncate" data-testid="docs-viewer-title">{previewDoc.name}</div>
              <div className="text-[10px] text-zinc-500">{fmtBytes(previewDoc.size_bytes)} · {previewDoc.mime_type}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => handleDownload(previewDoc)}
                className="text-xs text-zinc-300 hover:text-white flex items-center gap-1.5 px-3 py-1.5 rounded border border-white/10 hover:bg-white/5"
                data-testid="docs-viewer-download"
              >
                <Download className="w-3.5 h-3.5" /> Télécharger
              </button>
              <button
                onClick={() => setPreviewDoc(null)}
                className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5"
                data-testid="docs-viewer-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 p-3" onClick={(e) => e.stopPropagation()}>
            {isPreviewable(previewDoc.mime_type) ? (
              <iframe
                src={`${api.defaults.baseURL}/workspace/documents/${previewDoc.id}/preview`}
                title={previewDoc.name}
                className="w-full h-full rounded bg-white"
                data-testid="docs-viewer-frame"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center text-zinc-400 text-sm gap-3">
                <FileText className="w-12 h-12 text-[#378ADD] opacity-40" />
                <div>Aperçu non disponible pour ce format ({previewDoc.mime_type || "inconnu"}).</div>
                <button onClick={() => handleDownload(previewDoc)} className="btn-primary text-xs">
                  <Download className="w-3.5 h-3.5" /> Télécharger le fichier
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
